import { useDispatch } from "react-redux";
import type { SluggedId } from "Models";
import { adkitApi } from "../../api/adkitApi";
import { useUpdateAdKitMutation } from "../../api/endpoints/adkits";
import {
  useAddAdKitInstanceMutation,
  useDeleteAdKitInstanceMutation,
  useUpdateAdKitInstancesByLocationMutation,
} from "../../api/endpoints/instances";
import {
  useAddAdKitLocationMutation,
  useDeleteAdKitLocationMutation,
  useUpdateAdKitLocationMutation,
} from "../../api/endpoints/locations";
import type { CampaignEdit } from "./campaignEdits";

/** The {message} list a 201/207 envelope carries for items the API skipped. */
function envelopeErrors(response: unknown): string[] {
  const errors = (response as { errors?: unknown } | null)?.errors;
  if (!Array.isArray(errors)) return [];
  return errors
    .map((e) => (e as { message?: unknown })?.message)
    .filter((m): m is string => typeof m === "string" && m.trim() !== "");
}

/**
 * Runs planned edits in order. Stops at the first call that fails: what ran
 * before it is saved, and the refetched campaign shows exactly what is.
 */
export function useSaveCampaignEdits(facilityId: string) {
  const dispatch = useDispatch();
  const [updateAdKit] = useUpdateAdKitMutation();
  const [addLocations] = useAddAdKitLocationMutation();
  const [updateLocation] = useUpdateAdKitLocationMutation();
  const [deleteLocation] = useDeleteAdKitLocationMutation();
  const [addInstances] = useAddAdKitInstanceMutation();
  const [updateInstances] = useUpdateAdKitInstancesByLocationMutation();
  const [deleteInstance] = useDeleteAdKitInstanceMutation();

  const run = (adKitId: SluggedId, edit: CampaignEdit): Promise<unknown> => {
    switch (edit.kind) {
      case "updateTiming":
        return updateAdKit({
          facilityId,
          adKitId,
          body: {
            restoreDeleted: false,
            transitionFadeIn: edit.timing.fadeIn,
            transitionDisplay: edit.timing.display,
            transitionFadeOut: edit.timing.fadeOut,
          },
        }).unwrap();
      case "addLocations":
        return addLocations({ facilityId, adKitId, body: edit.body }).unwrap();
      case "deleteLocation":
        return deleteLocation({
          facilityId,
          adKitId,
          locationId: edit.locationId,
        }).unwrap();
      case "updateHoles":
        return updateLocation({
          facilityId,
          adKitId,
          locationId: edit.locationId,
          body: {
            adKitMetaDataId: edit.key,
            enabledHoles: edit.enabledHoles,
            overrideWarnings: true,
            restoreDeleted: false,
          },
        }).unwrap();
      case "deleteInstance":
        return deleteInstance({
          facilityId,
          adKitId,
          locationId: edit.locationId,
          instanceId: edit.instanceId,
        }).unwrap();
      case "reorderInstances":
        return updateInstances({
          facilityId,
          adKitId,
          locationId: edit.locationId,
          body: edit.order.map(({ id, orderLocation }) => ({
            id,
            orderLocation,
            overrideWarnings: true,
            restoreDeleted: false,
          })),
        }).unwrap();
      case "addInstances":
        return addInstances({
          facilityId,
          adKitId,
          locationId: edit.locationId,
          body: edit.body,
        }).unwrap();
    }
  };

  return async (adKitId: SluggedId, edits: CampaignEdit[]) => {
    const skipped: string[] = [];
    try {
      for (const edit of edits) {
        skipped.push(...envelopeErrors(await run(adKitId, edit)));
      }
    } finally {
      dispatch(adkitApi.util.invalidateTags(["AdKitList"]));
    }
    return { skipped };
  };
}
